import { affiliateApi, brandApi } from '../lib/axios';
// import { PaymentMethodFieldValue } from '../components/affiliate/PaymentInfoForm';

interface RequestPayoutPayload {
  programId: string;
}

// interface UpdateBankInfoPayload {
//   bankName: string;
//   accountHolderName: string;
//   bankAccountNumber: string;
//   bankRoutingNumber: string;
//   code: string;
//   bankAddress: string;
// }

// interface UpdatePaypalInfoPayload {
//   paypalEmail: string;
// }

export type PayoutStatus = 'PENDING' | 'PAID';

/** How a payout's amount divides between one-time and Recharge renewal
 *  commissions. Null on payouts made before sales were linked to payouts. */
export interface PayoutBreakdown {
  oneTimeAmount: number;
  recurringAmount: number;
  renewalCount: number;
}
export type PayoutUIStatus = 'Pending' | 'Paid';

export interface PaymentMethodField {
  id: string;
  fieldName: string;
  label: string;
  isRequired: boolean;
}

export interface PaymentMethodInfo {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
  fields: PaymentMethodField[];
}

export interface MatchingPaymentInfo {
  paymentMethod: PaymentMethodInfo;
  fieldData: Record<string, string>;
}

export interface PayoutRequest {
  affiliate: {
    firstName: string;
    lastName: string;
    email: string;
    profileImage: string | null;
  };
  id: string;
  amount: number;
  breakdown?: PayoutBreakdown | null;
  status: PayoutStatus;
  createdAt: string;
  updatedAt: string;
  paidAt: string | null;
  affiliateId: string;
  code: string;
  referenceNumber?: string | null;
  matchingPaymentInfos: MatchingPaymentInfo[];
  // UI specific fields
  name?: string;
  avatar?: string;
  uiStatus?: PayoutUIStatus;
  // New payment details structure
  paymentDetails?: {
    fieldData: {
      [key: string]: string;
    };
    paymentMethodId: string;
    paymentMethodName: string;
  };
  // Backward compatibility
  paymentMethod?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface PayoutHistory {
  id: string;
  name: string;
  username: string;
  avatar: string;
  payoutStatus: string;
  payoutDate: string;
  paymentMethod: string;
  paymentInformation: string;
  code: string;
  amount: number;
}

export interface AffiliatePayoutHistory {
  id: string;
  amount: number;
  breakdown?: PayoutBreakdown | null;
  status: PayoutStatus;
  createdAt: string;
  updatedAt: string;
  paidAt?: string | null;
  affiliateId: string;
  code: string;
  referenceNumber: string | null;
  // Payment method structure
  paymentDetails: {
    fieldData: Record<string, string>;
    paymentMethodId: string;
    paymentMethodName: string;
  };
  affiliate: {
    firstName: string;
    lastName: string;
    email: string;
    profileImage: string | null;
  };
  company: {
    companyName: string;
    description: string;
    profileImage: string;
    brandBannerImage: string;
    brandProfileImage: string;
  };
}

export interface BrandPayoutHistory {
  id: string;
  amount: number;
  breakdown?: PayoutBreakdown | null;
  createdAt: string;
  updatedAt: string;
  paidAt?: string | null;
  affiliateId: string;
  code: string;
  referenceNumber: string | null;
  paymentDetails: {
    fieldData: {
      [key: string]: string;
    };
    paymentMethodId: string;
    paymentMethodName: string;
  };
  affiliate: {
    firstName: string;
    lastName: string;
    email: string;
    profileImage: string | null;
  };
  status: PayoutStatus;
  matchingPaymentInfos?: MatchingPaymentInfo[];
}

interface PayoutStatistics {
  totalAvailableToWithdraw: number;
  totalRequested: number;
  totalProcessing: number;
  totalPaidOut: number;
  brandStatistics: {
    brandId: string;
    brandName: string;
    availableToWithdraw: number;
    requested: number;
    processing: number;
    totalPaidOut: number;
  }[];
}

interface ProgramPayoutStatusResponse {
  status: string;
  availableAmount: number;
  payoutAvailable: boolean;
  pendingRequest: number;
  totalPaid: string;
}

interface ProgramPayoutStatus {
  available: number;
  requested: number;
  paidOut: number;
}

const mapStatusToUIStatus = (status: PayoutStatus): PayoutUIStatus => {
  switch (status) {
    case 'PENDING':
      return 'Pending';
    case 'PAID':
      return 'Paid';
    default:
      return 'Pending';
  }
};

export const payoutService = {
  // Affiliate methods
  async requestPayout(data: RequestPayoutPayload) {
    const response = await affiliateApi.post('/payout/request-payout', data);
    return response.data;
  },

  // async updatePaymentMethods(paymentMethods: PaymentMethodValues) {
  //   const response = await affiliateApi.post('/affiliates/update-payment-methods', { paymentMethods });
  //   return response.data;
  // },

  // Brand methods
  async getPayoutRequests(page = 1, limit = 20, search = '', status: string = '') {
    const response = await brandApi.get<PaginatedResponse<PayoutRequest>>('/payout/requests', {
      params: {
        page,
        limit,
        ...(search ? { search } : {}),
        ...(status ? { status } : {})
      }
    });
    
    // Transform the response to match the table structure
    const transformedData = (response.data.data || []).map(request => {
      // Check if the request already has the new payment details structure
      if (request.paymentDetails && request.paymentDetails.paymentMethodName) {
        // Already has the new structure, just add UI-specific fields
        return {
          ...request,
          name: request.affiliate ? `${request.affiliate.firstName} ${request.affiliate.lastName}` : `Affiliate ${request.affiliateId}`,
          avatar: request.affiliate?.profileImage || '/avatars/avatar-1.png',
          uiStatus: mapStatusToUIStatus(request.status),
          paymentMethod: request.paymentDetails.paymentMethodName
        };
      }
      
      // Handle legacy format - get primary payment method if available
      const primaryPaymentMethod = request.matchingPaymentInfos && request.matchingPaymentInfos.length > 0 
        ? request.matchingPaymentInfos[0].paymentMethod.name 
        : undefined;
      
      // Get payment details from the first matching payment info
      const fieldData = request.matchingPaymentInfos && request.matchingPaymentInfos.length > 0
        ? request.matchingPaymentInfos[0].fieldData
        : {};
      
      // Create payment details in the new format
      const newPaymentDetails = request.matchingPaymentInfos && request.matchingPaymentInfos.length > 0 
        ? {
            fieldData,
            paymentMethodId: request.matchingPaymentInfos[0].paymentMethod.id,
            paymentMethodName: request.matchingPaymentInfos[0].paymentMethod.name
          }
        : undefined;

      const transformed = {
        ...request,
        name: request.affiliate ? `${request.affiliate.firstName} ${request.affiliate.lastName}` : `Affiliate ${request.affiliateId}`,
        avatar: request.affiliate?.profileImage || '/avatars/avatar-1.png',
        uiStatus: mapStatusToUIStatus(request.status),
        // Set payment details in the new format
        paymentDetails: newPaymentDetails,
        // Keep backward compatibility
        paymentMethod: primaryPaymentMethod
      };
      return transformed;
    });
    
    return {
      data: transformedData,
      pagination: response.data.pagination
    };
  },

  async getPayoutHistory(brandId: string) {
    const response = await brandApi.get<{ success: boolean; data: PayoutHistory[] }>(`/payout/brand/history/${brandId}`);
    return response.data.data || [];
  },

  async approvePayout(id: string) {
    const response = await brandApi.post(`/payout/approve/${id}`);
    return response.data;
  },

  async rejectPayout(id: string) {
    const response = await brandApi.post(`/payout/reject/${id}`);
    return response.data;
  },

  async bulkPayout(payoutData: { payoutId: string; paymentMethodId: string }[]) {
    const response = await brandApi.post('/payout/bulk-affiliate-payout', {
      payouts: payoutData
    });
    return response.data;
  },

  // Affiliate methods
  async getAffiliatePayoutHistory(page = 1, limit = 20) {
    const response = await affiliateApi.get<PaginatedResponse<AffiliatePayoutHistory>>('/payout/affiliate/history', {
      params: {
        page,
        limit
      }
    });
    return response.data || { data: [], pagination: { total: 0, page, limit, totalPages: 0 } };
  },

  async getBrandPayoutHistory(page = 1, limit = 20) {
    const response = await brandApi.get<PaginatedResponse<BrandPayoutHistory>>('/payout/brand/history', {
      params: {
        page,
        limit
      }
    });
    return {
      data: response.data.data || [],
      pagination: response.data.pagination
    };
  },

  /**
   * Updates the payment method of a paid payout request
   * @param payoutId The ID of the payout request
   * @param paymentMethodId The ID of the new payment method
   * @returns Promise with the updated payout data
   */
  async updatePayoutPaymentMethod(payoutId: string, paymentMethodId: string): Promise<any> {
    try {
      const response = await brandApi.post(`/payout/${payoutId}/update-payment-method`, { paymentMethodId });
      return response.data;
    } catch (error) {
      console.error('Error updating payout payment method:', error);
      throw error;
    }
  },

  async getProgramPayoutStatus(programId: string): Promise<ProgramPayoutStatus> {
    const response = await affiliateApi.get<ProgramPayoutStatusResponse>(`/payout/status/${programId}`);
    return {
      available: response.data.availableAmount,
      requested: response.data.pendingRequest,
      paidOut: parseFloat(response.data.totalPaid || '0')
    };
  },

  async getPayoutStatistics(): Promise<PayoutStatistics> {
    const response = await affiliateApi.get<PayoutStatistics>("/payout/statistics");
    return response.data;
  },

  /**
   * Toggles the status of a payout between PAID and PENDING
   * @param payoutId The ID of the payout request
   * @param status The new status to set (PAID or PENDING)
   * @returns Promise with the updated payout data
   */
  async togglePayoutStatus(payoutId: string, status: PayoutStatus): Promise<any> {
    try {
      const endpoint = status === 'PAID' ? `/payout/approve/${payoutId}` : `/payout/revert/${payoutId}`;
      const response = await brandApi.post(endpoint);
      return response.data;
    } catch (error) {
      console.error('Error toggling payout status:', error);
      throw error;
    }
  },
};